// Push Notifications card — shown on /admin/profile for staff roles.
// Lists this user's registered FCM devices and lets them:
//   - Send a test push to a specific device (verifies delivery).
//   - Remove a device they no longer use.
//   - Subscribe THIS browser session (if permission not yet granted).
//
// Reads from `GET /api/push/devices` (returns masked entries with full
// token included so the client can target test/unsubscribe calls).

import { useEffect, useState, useCallback } from "react";
import { API } from "../App";
import { Button } from "./ui/button";
import { Bell, Smartphone, Monitor, Send, Trash2, Loader2, BellRing, Plus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { setupStaffPushNotifications } from "../lib/push";

// Friendly platform → label/icon mapping.
function platformLabel(p) {
  if (p === "android") return { label: "Android (native)", Icon: Smartphone };
  if (p === "ios") return { label: "iOS (PWA)", Icon: Smartphone };
  return { label: "Web / PWA", Icon: Monitor };
}

// Parse a UA string into a compact "Chrome on Mac" style descriptor. Falls
// back to the platform label when nothing recognisable matches.
function describeUA(ua) {
  if (!ua) return "";
  const browser = ua.match(/Edg\/[\d.]+/) ? "Edge"
    : ua.match(/Chrome\/[\d.]+/) ? "Chrome"
    : ua.match(/Safari\/[\d.]+/) && !ua.match(/Chrome/) ? "Safari"
    : ua.match(/Firefox\/[\d.]+/) ? "Firefox"
    : "Browser";
  const os = ua.match(/iPhone|iPad/) ? "iOS"
    : ua.match(/Android/) ? "Android"
    : ua.match(/Windows/) ? "Windows"
    : ua.match(/Mac OS X|Macintosh/) ? "macOS"
    : ua.match(/Linux/) ? "Linux"
    : "";
  return os ? `${browser} on ${os}` : browser;
}

function relativeTime(iso) {
  if (!iso) return "";
  const diffMs = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diffMs / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function PushNotificationsCard() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyToken, setBusyToken] = useState(null);
  const [enabling, setEnabling] = useState(false);
  const [permission, setPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/push/devices`, { credentials: "include" });
      if (r.ok) setDevices(await r.json());
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchDevices(); }, [fetchDevices]);

  const handleEnable = async () => {
    setEnabling(true);
    try {
      const res = await setupStaffPushNotifications({ force: true });
      if (typeof Notification !== "undefined") setPermission(Notification.permission);
      if (res && res.ok) {
        toast.success("This device is now subscribed.");
        // Give the backend a beat to receive the token post.
        setTimeout(fetchDevices, 1500);
      } else {
        const reason = res?.reason || "unknown";
        if (reason === "permission-denied" || reason === "permission-default") {
          toast.error(
            "Notifications are blocked. Click the lock icon in the address bar → Site Settings → Notifications → Allow, then try again.",
            { duration: 8000 }
          );
        } else if (reason === "not-configured") {
          toast.error("Push is not configured for this site. Contact support.");
        } else if (reason === "unsupported") {
          toast.error("This browser does not support push notifications.");
        } else if (reason === "no-token") {
          toast.error("Could not retrieve a device token. Try reloading the page.");
        } else if (reason === "error") {
          const detail = res?.code || res?.message || "see browser console";
          toast.error(`Push enable failed: ${detail}`, { duration: 10000 });
        } else {
          toast.error(`Could not enable push (${reason}).`);
        }
      }
    } catch (e) {
      toast.error("Could not enable push on this device.");
    } finally { setEnabling(false); }
  };

  const handleTest = async (token) => {
    setBusyToken(token);
    try {
      const r = await fetch(`${API}/push/test`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ token }),
      });
      const j = await r.json().catch(() => ({}));
      if (r.status === 410) {
        toast.error("Device token expired and was removed.");
        fetchDevices();
      } else if (!r.ok) {
        toast.error(j.detail || "Test push failed");
      } else {
        toast.success("Test sent — check your phone in a few seconds.");
      }
    } catch (e) {
      toast.error("Network error");
    } finally { setBusyToken(null); }
  };

  const handleRemove = async (token) => {
    if (!window.confirm("Remove this device? You'll stop receiving pushes here until you re-enable.")) return;
    setBusyToken(token);
    try {
      const r = await fetch(`${API}/push/unsubscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ token }),
      });
      if (r.ok) {
        toast.success("Device removed.");
        fetchDevices();
      } else {
        toast.error("Could not remove device");
      }
    } catch { toast.error("Network error"); }
    finally { setBusyToken(null); }
  };

  const showEnableCta = permission !== "granted" || devices.length === 0;

  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-5" data-testid="push-notifications-card">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
            <BellRing size={16} className="text-emerald-700" />
          </div>
          <h2 className="text-base font-bold text-gray-900">Push Notifications</h2>
        </div>
        <button
          onClick={fetchDevices}
          className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-50"
          title="Refresh"
          data-testid="push-refresh-btn"
        >
          <RefreshCw size={14} />
        </button>
      </div>
      <p className="text-[11px] text-gray-500 mb-4">
        Each browser / phone is a separate device. Send a test ping to verify it'll wake up on a new order.
      </p>

      {showEnableCta && (
        <div className="mb-4 rounded-xl border border-emerald-100 bg-emerald-50/50 p-3 flex items-center justify-between gap-3" data-testid="push-enable-cta">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-emerald-900">Enable on this device</p>
            <p className="text-[11px] text-emerald-700/80">
              {permission === "denied"
                ? "Notifications are blocked in your browser settings — unblock them, then click below."
                : "Get instant alerts for new orders even when the app is closed."}
            </p>
          </div>
          <Button
            size="sm"
            onClick={handleEnable}
            disabled={enabling}
            className="bg-emerald-600 hover:bg-emerald-700 text-white flex-shrink-0"
            data-testid="push-enable-btn"
          >
            {enabling ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Plus size={14} className="mr-1.5" />}
            {enabling ? "Enabling…" : "Enable"}
          </Button>
        </div>
      )}

      {loading ? (
        <div className="py-6 flex items-center justify-center text-gray-400 text-xs">
          <Loader2 size={14} className="animate-spin mr-2" /> Loading devices…
        </div>
      ) : devices.length === 0 ? (
        <div className="py-6 text-center text-xs text-gray-400">
          <Bell size={20} className="mx-auto mb-2 text-gray-300" />
          No devices subscribed yet.
        </div>
      ) : (
        <ul className="space-y-2">
          {devices.map((d) => {
            const { label, Icon } = platformLabel(d.platform);
            const ua = describeUA(d.user_agent);
            return (
              <li
                key={d.fcm_token_id}
                className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:bg-gray-50/50"
                data-testid={`push-device-${d.fcm_token_id}`}
              >
                <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <Icon size={16} className="text-gray-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {ua || label}
                  </p>
                  <p className="text-[10px] text-gray-400 truncate font-mono">
                    {d.token_preview} · {relativeTime(d.updated_at)}
                  </p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleTest(d.token)}
                    disabled={busyToken === d.token}
                    className="h-8 px-2 text-[11px]"
                    data-testid={`push-test-${d.fcm_token_id}`}
                  >
                    {busyToken === d.token ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                    <span className="ml-1 hidden sm:inline">Test</span>
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleRemove(d.token)}
                    disabled={busyToken === d.token}
                    className="h-8 px-2 text-[11px] text-red-600 hover:text-red-700 hover:bg-red-50 border-red-100"
                    data-testid={`push-remove-${d.fcm_token_id}`}
                  >
                    <Trash2 size={12} />
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
